import { Injectable, computed, signal } from '@angular/core';
import { BehaviorSubject, defer, distinctUntilChanged, filter, firstValueFrom, Observable, shareReplay } from 'rxjs';
import { HttpService } from 'src/app/core/http/http.service';
import { MedicalRecordState } from 'src/app/features/medical-record/store/medical-record.state';
import { Store } from '@ngrx/store';
import { clearAnamnesisQuestionnaireResponse, clearEvolutionQuestionnaireResponse, clearToxicityQuestionnaireResponse, clearVitalSignsQuestionnaireResponse } from 'src/app/features/medical-record/store/medical-record.action';
import { environment } from 'src/environments/environment';
import { PatientAPI, PatientInfoAPI } from 'src/app/features/medical-record/medical-record-page/pages/patient-informations/model/patient.model';
import { MedicalRecordService } from 'src/app/features/medical-record/services/medical-record.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class PatientService {

  private _patientId = new BehaviorSubject<string | null>(null);
  private _selectedPatient = new BehaviorSubject<PatientInfoAPI | null>(null);
  private _selectedPatientRisks = new BehaviorSubject<string[]>([]);
  private _loadingPatient = new BehaviorSubject<boolean>(false);

  private selecTedPatientSignal = signal<PatientInfoAPI | null>(null)

  // defaultPatient  = '2896a032-6b70-44ff-bbf1-b540a3034841'; // contem historico
  // defaultPatient  = 'ecc5da6c-a31f-4c94-9811-f83aeedc7823'; //sem historico
  private defaultPatient = 'df0927c6-323d-47d8-bc3c-d61a2dc748a4' // problema sinais vitais
  // defaultPatient =  'e224d5e2-aef6-4b83-97a7-676f899c4f20' // problema sinais vitais
  // defaultPatient =  '8cae4ac5-cdb3-4a8f-841f-38d28afe6639' // problema sinais vitais

  constructor(
    private httpService: HttpService,
    private router: Router,
  ) {

    // só mudar pra paciente padrão se estiver em medical record
    const lastPatient = localStorage.getItem('lastPatient') ?? 'null'
    // console.log({url : this.router.url})
    if (this.router.url !== '/medical_record') {
      return
    }
    if (lastPatient !== 'null') {
      this.setSelectedPatientById(lastPatient)
    } else {
      this.setSelectedPatientById(this.defaultPatient);
    }
    // else if (environment.title === 'homologation') {
    //   this.setSelectedPatientById('2896a032-6b70-44ff-bbf1-b540a3034841');
    // }
  }


  // todo: talvez seja melhor combinar esse obs com o do paciente e até o de id e criar
  // um union type como {type: 'loading' | 'null' | 'loaded' , data: ... }
  // para ser facil os demais comp saberem, com um unico obs , o que ocorre com o paciente select,
  // tbm evita a chance de erros por esquecer de mudar uma das variaveis manualmente (como esquecer de colocar loading = false em algum condicional por ex)
  get isLoadingPatientInfo(): Observable<boolean> {
    return this._loadingPatient.asObservable()
  }

  isLoadingPatientSignal = toSignal(this._loadingPatient)

  get selectedPatientSignal() {
    return this.selecTedPatientSignal.asReadonly()
  }

  isFirstAppointment = computed(() => {
    const pat = this.selectedPatientSignal();
    if (pat == null) {
      return null
    }
    const status = pat.patientMedicalRecord.diagnosisStatus.toLowerCase().trim();
    return status === '' || status == 'inicial' || status == 'primeira consulta'
  })

  get selectedPatientObs(): Observable<PatientInfoAPI | null> {
    return this._selectedPatient.asObservable();
  }

  get selectedPatientRisksObs(): Observable<string[]> {
    return this._selectedPatientRisks.asObservable();
  }

  get selectedPatient() {
    return firstValueFrom(this.selectedPatientObs)
  }

  // só envia/despacha null se ainda não foi selecionado nenhum paciente
  get nonNullSelectedPatient() {
    return this._selectedPatient.pipe(
      filter(pat => pat !== null),
      distinctUntilChanged()
    );
  }

  async setSelectedPatientById(id: string | null, { forceRefresh } = { forceRefresh: false }) {

    if (id === null) {
      this._loadingPatient.next(false);
      this._selectedPatient.next(null);
      this._selectedPatientRisks.next([]);
      this.selecTedPatientSignal.set(null)
      return;
    }
    const currentPatient = await this.selectedPatient;
    if (currentPatient?.patientId === id && !forceRefresh) {
      return;
    }

    localStorage.setItem('lastPatient', id ?? 'null')

    this._patientId.next(id);
    this._loadingPatient.next(true);
    this._selectedPatient.next(null);
    this._selectedPatientRisks.next([]);
    this.selecTedPatientSignal.set(null);
    try {

      const [patientInfo, patientRisks] = await Promise.all([
        this.fetchPatientInfo(id),
        this.fetchPatientRisks(id)
      ]);
      this._selectedPatient.next(patientInfo);

      this._selectedPatientRisks.next(patientRisks);
      this.selecTedPatientSignal.set(patientInfo);
    } catch (e) {
      console.error(e);
      // TODO: chamar componente de aviso/alert e avisar
    }
    this._loadingPatient.next(false);

  }

  getRisksNames(patientRisks: string[]) {

  }

  async searchPatients(filter: { name: string } | { identifier: string }) {
    const filterParams = filter.hasOwnProperty('name') ? 'patientByName' : 'patient';
    const apiVersion = filterParams === 'patient' ? 1 : 2;
    const method = filterParams === 'patient' ? 'post' : 'get';

    const result$ = defer(() =>
      this.httpService.req<PatientAPI[]>(filterParams, 'patient', {
        apiVersion,
        params: {
          ...filter,
          lg: 'pt-BR'
        },
        method
      })
    )

    const result = await firstValueFrom(result$)
    return result;
  }

  private async fetchPatientInfo(patientId: string) {

    const res = await this.httpService.req<PatientInfoAPI>('patientInfo', 'patient', {
      apiVersion: 2,
      params: {
        patientId,
        lg: 'pt-BR'
      },
      loaderKey: 'patient',
      method: 'get'
    });

    if (res.status === 'notok') {
      throw res.internalStatusCode
    }
    if (!res.result) {
      throw res.internalStatusCode
    }

    return res.result

  }

  private async fetchPatientRisks(patientId: string) {

    const res = await this.httpService.req<string[]>('patientRisks', 'patient', {
      apiVersion: 2,
      params: {
        patientId,
        lg: 'pt-BR'
      },
      loaderKey: 'patient',
      method: 'get'
    });

    if (res.status === 'notok') {
      throw res.internalStatusCode
    }
    if (!res.result) {
      throw res.internalStatusCode
    }

    return res.result

  }

  getSelectedPatient() {
    return this._selectedPatient.asObservable();
  }

  getPatientId() {
    return this._patientId.asObservable();
  }

  getPatientRisks() {
    return this._selectedPatientRisks.asObservable();
  }

}
